import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { Check, Zap, Save } from "lucide-react";
import { toast } from "sonner";
import AppHeader from "@/components/AppHeader";
import ThreeCircuitStep1 from "@/components/certificates/ThreeCircuitStep1";
import ThreeCircuitStep2 from "@/components/certificates/ThreeCircuitStep2";
import ThreeCircuitStep3 from "@/components/certificates/ThreeCircuitStep3";
import RecentAddressPicker from "@/components/certificates/RecentAddressPicker";
import { saveDraft, loadDraft, deleteDraft } from "@/utils/offlineStorage";
import { authFetch } from "@/utils/authFetch";

const API = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  { num: 1, label: "Works & Earthing" },
  { num: 2, label: "Circuits & Testing" },
  { num: 3, label: "Review & Generate" },
];

const EMPTY_CIRCUIT = {
  db_ref: "", circuit_number: "", circuit_description: "", reference_method: "",
  conductor_live_size: "", conductor_cpc_size: "", ocpd_type: "", ocpd_rating: "",
  rcd_type: "", rcd_idn: "", r1r2: "", max_zs: "", ir_test_voltage: "500",
  ir_live_live: "", ir_live_earth: "", polarity: "N/A", rcd_disconnect_time: "", rcd_test_btn: "N/A",
};

const INITIAL_FORM = {
  client_name: "", client_email: "", description_of_works: "",
  installation_address: "", installation_postcode: "",
  departures: "", comments_existing: "", risk_assessment: "N/A",
  system_earthing: "", zdb: "", adequate_main_conductors: "",
  bonding_water: "N/A", bonding_gas: "N/A", bonding_oil: "N/A",
  bonding_structural: "N/A", bonding_other: "", earthing_conductor: "",
  circuits: [{ ...EMPTY_CIRCUIT }, { ...EMPTY_CIRCUIT }, { ...EMPTY_CIRCUIT }],
  electrician_name: "", cert_date: null,
  contractor_trading_title: "", contractor_email: "", contractor_phone: "",
  contractor_address: "", contractor_postcode: "",
  contractor_reg_no: "", contractor_branch_no: "",
};

export default function ThreeCircuitMWWizard() {
  const { user } = useAuth();
  const [step, setStep] = useState(1);
  const [draftSaved, setDraftSaved] = useState(false);
  const draftKey = "draft_three_circuit_mw";

  const [formData, setFormData] = useState({
    ...INITIAL_FORM,
    electrician_name: user?.name || "",
    contractor_trading_title: user?.company || "",
    contractor_email: user?.email || "",
  });

  // Auto-save draft every 10s
  useEffect(() => {
    const timer = setInterval(() => {
      saveDraft(draftKey, { formData }).then(() => setDraftSaved(true));
    }, 10000);
    return () => clearInterval(timer);
  }, [formData]);

  const manualSaveDraft = async () => {
    await saveDraft(draftKey, { formData });
    setDraftSaved(true);
    toast.success("Draft saved locally");
  };

  useEffect(() => {
    const dupData = sessionStorage.getItem("duplicate_cert_data");
    if (dupData) {
      try {
        const cert = JSON.parse(dupData);
        sessionStorage.removeItem("duplicate_cert_data");
        setFormData((prev) => ({ ...prev, ...cert, cert_date: null }));
        return;
      } catch { /* ignore */ }
    }
    // Try loading a saved draft
    loadDraft(draftKey).then((draft) => {
      if (draft && draft.formData) {
        setFormData((prev) => ({ ...prev, ...draft.formData }));
        toast.info("Restored from saved draft");
        return;
      }
    }).catch(() => {});
    authFetch(`${API}/api/user/contractor-details`, { credentials: "include" })
      .then((r) => r.ok ? r.json() : {})
      .then((saved) => {
        if (saved && saved.contractor_trading_title) {
          setFormData((prev) => ({
            ...prev,
            ...Object.fromEntries(Object.entries(saved).filter(([, v]) => v)),
          }));
        }
      })
      .catch(() => {});
  }, []);

  const updateForm = (updates) => setFormData((prev) => ({ ...prev, ...updates }));
  const nextStep = () => setStep((s) => Math.min(s + 1, 3));
  const prevStep = () => setStep((s) => Math.max(s - 1, 1));

  const handleAddressPick = (addr) => {
    updateForm({
      installation_address: addr.address,
      installation_postcode: addr.postcode,
      client_name: addr.client_name || formData.client_name,
      client_email: addr.client_email || formData.client_email,
    });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-page)' }} data-testid="three-circuit-mw-wizard">
      <AppHeader />
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex items-center gap-3 mb-4">
          <Zap className="w-6 h-6 text-[#00A3E0]" />
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Three Circuit Minor Works Certificate
          </h1>
        </div>

        <RecentAddressPicker onSelect={handleAddressPick} />

        <div className="flex items-center justify-between mb-8 mt-6" data-testid="tcmw-step-indicator">
          {STEPS.map((s, i) => (
            <div key={s.num} className="flex items-center flex-1">
              <div className="flex flex-col items-center">
                <div className={`w-10 h-10 flex items-center justify-center border-2 text-sm font-bold transition-all ${
                  step > s.num ? "step-completed" : step === s.num ? "step-active" : "step-inactive"
                }`} style={{ fontFamily: 'Outfit, sans-serif' }}>
                  {step > s.num ? <Check className="w-5 h-5" /> : s.num}
                </div>
                <span className={`text-xs mt-2 font-medium text-center hidden sm:block ${step >= s.num ? "" : "text-[#B8B9BD]"}`}
                  style={{ fontFamily: 'Outfit, sans-serif', color: step >= s.num ? 'var(--text-primary)' : undefined }}>
                  {s.label}
                </span>
              </div>
              {i < STEPS.length - 1 && <div className={`flex-1 h-0.5 mx-2 sm:mx-4 ${step > s.num ? "bg-[#00A3E0]" : "bg-[#E4E4E7]"}`} />}
            </div>
          ))}
        </div>

        <div className="flex justify-end mb-3">
          <button type="button" onClick={manualSaveDraft} className="brutalist-btn-outline !px-3 !py-1.5 text-xs flex items-center gap-1.5" data-testid="tcmw-save-draft-btn">
            <Save className="w-3.5 h-3.5" /> {draftSaved ? "Draft Saved" : "Save Draft"}
          </button>
        </div>

        <div className="animate-fade-in-up" key={step}>
          {step === 1 && <ThreeCircuitStep1 formData={formData} updateForm={updateForm} onNext={nextStep} />}
          {step === 2 && <ThreeCircuitStep2 formData={formData} updateForm={updateForm} onNext={nextStep} onPrev={prevStep} />}
          {step === 3 && <ThreeCircuitStep3 formData={formData} onPrev={prevStep} onGenerated={() => deleteDraft(draftKey).catch(() => {})} />}
        </div>
      </div>
    </div>
  );
}
